import client from '@/lib/axios-client'
import type {
	VereAgirAllListOutput,
	VereAgirAllListParams,
	VereAgirCreateInput,
	VereAgirListOutput,
	VereAgirListParams,
	VereAgirMeListOutput,
	VereAgirMeListParams,
	VereAgirOutput,
	VereAgirUpdateInput,
} from '@/types'
import {
	assertUploadSizeWithinLimit,
	DEFAULT_MAX_SIZE_MB,
	optimizeImageForUpload,
	sendWithCompressionRetry,
} from '@/utils/image-compression'

const VERE_AGIR_URL = '/ver-e-agir'
const VERE_AGIR_ME_URL = '/ver-e-agir/me'
const VERE_AGIR_ALL_URL = '/ver-e-agir/all'

export async function createVereAgir(
	data: VereAgirCreateInput,
): Promise<VereAgirOutput> {
	const send = async (maxSizeMB: number) => {
		const formData = new FormData()
		formData.append('crId', data.crId)
		if (data.customerId) {
			formData.append('customerId', data.customerId)
		}
		if (data.customerClassificationId) {
			formData.append('customerClassificationId', data.customerClassificationId)
		}
		formData.append('date', data.date)
		formData.append('kindId', data.kindId)
		formData.append('riskId', data.riskId)
		const potential = data.potential
		if (potential) {
			formData.append('potential', potential)
		}
		formData.append('description', data.description)
		formData.append('immediateAction', data.immediateAction)
		formData.append('responsibleId', data.responsibleId)
		formData.append('dueDate', data.dueDate)
		formData.append('status', data.status)
		formData.append('situation', data.situation)
		if (data.image) {
			const optimizedImage = await optimizeImageForUpload(data.image, maxSizeMB)
			assertUploadSizeWithinLimit([optimizedImage])
			formData.append('image', optimizedImage)
		}

		const response = await client.post(VERE_AGIR_URL, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		})
		return response.data.data
	}

	// Sem imagem, recomprimir nao resolve um 413 — envia sem retry
	return data.image instanceof File
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function updateVereAgir(
	id: string,
	data: VereAgirUpdateInput,
): Promise<VereAgirOutput> {
	const send = async (maxSizeMB: number) => {
		const formData = new FormData()

		for (const key of Object.keys(data) as (keyof VereAgirUpdateInput)[]) {
			const val = data[key]
			if (val === undefined || val === null) {
				continue
			}
			if (
				(key === 'image' || key === 'resolutionImage') &&
				val instanceof File
			) {
				const optimizedImage = await optimizeImageForUpload(val, maxSizeMB)
				assertUploadSizeWithinLimit([optimizedImage])
				formData.append(key, optimizedImage)
			} else {
				formData.append(String(key), String(val))
			}
		}

		const response = await client.patch(`${VERE_AGIR_URL}/${id}`, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})
		return response.data.data
	}

	return data.image instanceof File || data.resolutionImage instanceof File
		? sendWithCompressionRetry(send)
		: send(DEFAULT_MAX_SIZE_MB)
}

export async function getVereAgirList(
	params?: VereAgirListParams,
): Promise<VereAgirListOutput> {
	const response = await client.get(VERE_AGIR_URL, { params })
	return response.data.data
}

export async function getVereAgirMeList(
	params?: VereAgirMeListParams,
): Promise<VereAgirMeListOutput> {
	const response = await client.get(VERE_AGIR_ME_URL, { params })
	return response.data.data
}

export async function getVereAgirAllList(
	params?: VereAgirAllListParams,
): Promise<VereAgirAllListOutput> {
	const response = await client.get(VERE_AGIR_ALL_URL, { params })
	return response.data.data
}

export async function getVereAgirById(id: string): Promise<VereAgirOutput> {
	const response = await client.get(`${VERE_AGIR_URL}/${id}`)
	return response.data.data
}

export async function deleteVereAgir(id: string): Promise<void> {
	await client.delete(`${VERE_AGIR_URL}/${id}`)
}
